// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineNotes, { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import CartLineContainer from "@pacific/App/Components/Cart/CartLineContainer";
import cartPageStyles from "@pacific/App/SharedStyle/CartPageStyles";
import ProductAvailability, { ProductAvailabilityStyles } from "@pacific/Overrides/Components/ProductAvailability";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@pacific/Overrides/Components/ProductPartNumbers";
import { color } from "@pacific/XDL/Utilities";
import React, { useContext } from "react";
import { ResolveThunks, connect } from "react-redux";
import { css } from "styled-components";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    editable: boolean;
    showInvalidPriceMessage?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    showInventoryAvailability?: boolean;
    showAddToList?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
    wishListSettings: getSettingsCollection(state).wishListSettings,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardExpandedStyles {
    cartLineNotes?: CartLineNotesStyles;
    price?: ProductPriceStyles;
    quantity?: CartLineQuantityStyles;
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoContainer?: GridContainerProps;
    productDescriptionAndPartNumbersGridItem?: GridItemProps;
    productDescriptionAndPartNumbersContainer?: GridContainerProps;
    productBrand?: ProductBrandStyles;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    orderLineNotesGridItem?: GridItemProps;
    notesHeadingAndText?: SmallHeadingAndTextStyles;
    costCodeGridItem?: GridItemProps;
    costCodeValueText?: TypographyPresentationProps;
    costCodeLabelText?: TypographyPresentationProps;
    productPriceAndQuantityGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionNameText?: TypographyProps;
    quantityGridItem?: GridItemProps;
    qtyHeadingAndText?: SmallHeadingAndTextStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPrice?: SmallHeadingAndTextStyles;
    subtotalWithoutVatText?: SmallHeadingAndTextStyles;
    vatAmount?: SmallHeadingAndTextStyles;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    // XNG-Changes
    totalSmallHeadingAndText?: SmallHeadingAndTextStyles;
    vatLabelText?: TypographyPresentationProps;
    productAvailability?: ProductAvailabilityStyles;
    addToListLink?: LinkPresentationProps;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    productImage: {
        ...cartPageStyles.cartLine?.productImage,
    },
    productDescription: {
        ...cartPageStyles.cartLine?.productDescription,
    },
    quantity: {
        ...cartPageStyles.cartLine?.quantity,
    },

    price: {
        ...cartPageStyles.cartLine?.productPrice,
    },
    cartLineErrorMessageGridItem: {
        width: 12,
    },
    cartLineErrorMessageText: {
        color: "danger",
    },
    totalSmallHeadingAndText: {
        ...cartPageStyles.cartLine?.totalSmallHeadingAndText,
    },
    vatLabelText: {
        size: 12,
    },
    productAvailability: {
        ...cartPageStyles.cartLine?.productAvailability,
    },
    addToListLink: {
        ...cartPageStyles.cartLine?.addToListLink,
    },
};

const CartLineCardExpanded = ({
    cart,
    cartLine,
    showSavingsAmount,
    showSavingsPercent,
    showInventoryAvailability,
    promotions,
    editable,
    enableVat,
    vatPriceDisplay,
    showInvalidPriceMessage,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    extendedStyles,
    showAddToList,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));
    const toasterContext = useContext(ToasterContext);

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    return (
        <CartLineContainer
            extendedStyles={{
                container: {
                    css: css`
                        ${cartLine.isRestricted ? `background: ${color("danger.light")}` : ""};
                    `,
                },
            }}
        >
            <CartLineContainer.Image>
                <ProductImage product={cartLine} extendedStyles={styles.productImage} />
            </CartLineContainer.Image>
            <CartLineContainer.Description>
                {cartLine.brand && <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />}
                <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                <ProductPartNumbers
                    productNumber={cartLine.manufacturerItem ? "" : cartLine.erpNumber}
                    manufacturerItem={cartLine.manufacturerItem}
                    customerProductNumber={cartLine.customerName}
                    extendedStyles={styles.productPartNumbers}
                    uniqueProductCode={(cartLine as any).upcCode}
                />
            </CartLineContainer.Description>

            <CartLineContainer.Availability>
                {showInventoryAvailability && !cartLine.quoteRequired && (
                    <ProductAvailability
                        productId={cartLine.productId!}
                        availability={cartLine.availability!}
                        unitOfMeasure={cartLine.unitOfMeasure}
                        trackInventory={cartLine.trackInventory}
                        failedToLoadInventory={cart.failedToGetRealTimeInventory}
                        extendedStyles={styles.productAvailability}
                        hideLink={true}
                        qtyOnHand={cartLine.qtyOnHand}
                    />
                )}
            </CartLineContainer.Availability>
            <CartLineContainer.AvailabilityLink>
                {showInventoryAvailability && !cartLine.quoteRequired && (
                    <ProductAvailability
                        productId={cartLine.productId!}
                        availability={cartLine.availability!}
                        unitOfMeasure={cartLine.unitOfMeasure}
                        trackInventory={cartLine.trackInventory}
                        failedToLoadInventory={cart.failedToGetRealTimeInventory}
                        extendedStyles={styles.productAvailability}
                        linkOnly={true}
                    />
                )}
            </CartLineContainer.AvailabilityLink>

            <CartLineContainer.Price>
                {!cart.cartNotPriced && (
                    <ProductPrice
                        product={cartLine}
                        currencySymbol={cart.currencySymbol}
                        showSavings={false}
                        showLabel={true}
                        showSavingsAmount={false}
                        showSavingsPercent={false}
                        showInvalidPriceMessage={true}
                        extendedStyles={styles.price}
                        {...{ labelOverride: translate("Price/Unit") }}
                    />
                )}
            </CartLineContainer.Price>
            <CartLineContainer.Qty>
                <CartLineQuantity
                    cart={cart}
                    editable={editable}
                    extendedStyles={styles.quantity}
                    {...{ showLabel: true }}
                />
            </CartLineContainer.Qty>
            <CartLineContainer.Total>
                {!cartLine.quoteRequired && !cart.cartNotPriced && cartLine.pricing && (
                    <StyledWrapper {...styles.extendedUnitNetPriceGridItem}>
                        <SmallHeadingAndText
                            extendedStyles={styles.totalSmallHeadingAndText}
                            data-test-selector="cartline_extendedUnitNetPrice"
                            heading={translate("Total")}
                            text={
                                enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                    : cartLine.pricing.extendedUnitNetPriceDisplay
                            }
                        />
                        {enableVat && (
                            <>
                                <Typography as="p" {...styles.vatLabelText}>
                                    {vatPriceDisplay === "DisplayWithVat" ||
                                    vatPriceDisplay === "DisplayWithAndWithoutVat"
                                        ? `${translate("Inc. VAT")} (${cartLine.pricing.vatRate}%)`
                                        : translate("Ex. VAT")}
                                </Typography>
                                {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                    <>
                                        <Typography {...styles.subtotalWithoutVatText}>
                                            {cartLine.pricing.extendedUnitNetPriceDisplay}
                                        </Typography>
                                        <Typography as="p" {...styles.vatLabelText}>
                                            {translate("Ex. VAT")}
                                        </Typography>
                                    </>
                                )}
                            </>
                        )}
                    </StyledWrapper>
                )}
            </CartLineContainer.Total>

            <CartLineContainer.LineNotes>
                <CartLineNotes
                    cart={cart}
                    editable={editable}
                    extendedStyles={styles.cartLineNotes}
                    // XNG-Changes
                    {...{ noteFieldsVisible: true }}
                />
            </CartLineContainer.LineNotes>
            {showAddToList && (
                <CartLineContainer.AddToList>
                    <Link {...styles.addToListLink} onClick={addToListClickHandler} aria-label="Add to List">
                        {translate("Add to List")}
                    </Link>
                </CartLineContainer.AddToList>
            )}
        </CartLineContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
